import { createSlice, current } from '@reduxjs/toolkit'

import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { notDefined } from '../../utils'

export const deadlineCategory = 'date'
export const discoverCategory = 'check'
export const interestingsCategory = 'interestings'
export const coninfoCategory = 'coninfo'
export const deadlineAcknowledgmentRequiredMode = 'deadline_acknowledgment_required'

export const requiresDeadlineAcknowledgment = (notification) => {
  return notification?.deadline_metadata?.mode === deadlineAcknowledgmentRequiredMode
}

export const makeNotificationTaskHash = (checksum, notification) => {
  const id = notification?.id ?? ''
  const published = notification?.published ?? ''
  const category = notification?.category ?? ''
  return `${checksum}:${id}:${published}:${category}`
}

const requiresDiscoverAcknowledgment = (notification) => {
  return notification?.category === discoverCategory
}

const getTrackedNotifications = (data) => {
  return data.filter((notification) => {
    return requiresDiscoverAcknowledgment(notification) ||
      (notification?.category === deadlineCategory && requiresDeadlineAcknowledgment(notification))
  })
}

const createTrackedTaskState = (data, checksum, previousState = {}) => {
  const nextState = {}
  const tracked = getTrackedNotifications(data)
  tracked.forEach((notification) => {
    const hash = makeNotificationTaskHash(checksum, notification)
    nextState[hash] = previousState[hash] === true
  })
  return nextState
}

const countRemainingTrackedTasks = (data, checksum, trackedTaskState = {}) => {
  const tracked = getTrackedNotifications(data)
  return tracked.reduce((sum, notification) => {
    const hash = makeNotificationTaskHash(checksum, notification)
    return trackedTaskState[hash] === true ? sum : sum + 1
  }, 0)
}

const countRemainingTrackedTasksByCategory = (data, checksum, trackedTaskState = {}) => {
  const tracked = getTrackedNotifications(data)
  return tracked.reduce((counts, notification) => {
    const hash = makeNotificationTaskHash(checksum, notification)
    if (trackedTaskState[hash] === true) {
      return counts
    }

    const category = notification?.category ?? ''
    if (category === '') {
      return counts
    }

    return {
      ...counts,
      [category]: (counts[category] ?? 0) + 1
    }
  }, {})
}

const initialState = {
  data: [],
  error: '',
  checksum: '',
  updated: '',
  // Legacy keys kept for persisted-state compatibility.
  deadlineTaskState: {},
  deadlineTasksRemaining: 0,
  deadlineTasksRemainingByCategory: {},
  trackedTaskState: {},
  trackedTasksRemaining: 0,
  trackedTasksRemainingByCategory: {}
}

const notifications = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    fetchNotificationsSuccess (state, action) {
      const { data, checksum, generated } = action.payload
      const {
        category,
        trackedTaskState = {},
        // Keep compatibility with previously persisted state keys.
        deadlineTaskState: legacyTrackedTaskState = {}
      } = current(state)
      const previousTrackedTaskState = {
        ...legacyTrackedTaskState,
        ...trackedTaskState
      }
      const categories = [...new Set(data.map((element) => element.category)), 'coninfo']
      let useCategory = category
      if (notDefined(useCategory) && categories.length > 0) {
        useCategory = categories[0]
      }
      const nextTrackedTaskState = createTrackedTaskState(data, checksum, previousTrackedTaskState)
      const trackedTasksRemaining = countRemainingTrackedTasks(data, checksum, nextTrackedTaskState)
      const trackedTasksRemainingByCategory = countRemainingTrackedTasksByCategory(data, checksum, nextTrackedTaskState)
      return {
        ...state,
        data,
        categories,
        category: useCategory,
        checksum,
        updated: generated,
        deadlineTaskState: nextTrackedTaskState,
        deadlineTasksRemaining: trackedTasksRemaining,
        deadlineTasksRemainingByCategory: trackedTasksRemainingByCategory,
        trackedTaskState: nextTrackedTaskState,
        trackedTasksRemaining,
        trackedTasksRemainingByCategory,
        error: ''
      }
    },
    fetchNotificationsError (state, action) {
      const { detail } = action.payload

      return {
        ...state,
        error: detail
      }
    },
    useCategory (state, action) {
      const { category } = action.payload
      return {
        ...state,
        category
      }
    },
    confirmDeadlineTask (state, action) {
      const { hash, completed = true } = action.payload
      if (notDefined(hash) || hash === '') {
        return state
      }

      const {
        data,
        checksum,
        trackedTaskState = {},
        // Keep compatibility with previously persisted state keys.
        deadlineTaskState: legacyTrackedTaskState = {}
      } = current(state)
      const nextTrackedTaskState = {
        ...legacyTrackedTaskState,
        ...trackedTaskState,
        [hash]: completed === true
      }
      const trackedTasksRemaining = countRemainingTrackedTasks(data, checksum, nextTrackedTaskState)
      const trackedTasksRemainingByCategory = countRemainingTrackedTasksByCategory(data, checksum, nextTrackedTaskState)
      return {
        ...state,
        deadlineTaskState: nextTrackedTaskState,
        deadlineTasksRemaining: trackedTasksRemaining,
        deadlineTasksRemainingByCategory: trackedTasksRemainingByCategory,
        trackedTaskState: nextTrackedTaskState,
        trackedTasksRemaining,
        trackedTasksRemainingByCategory
      }
    }

  }
})

const persistConfig = {
  key: 'notifications',
  storage
}

export const { fetchNotificationsSuccess, fetchNotificationsError, useCategory, confirmDeadlineTask } =
  notifications.actions
export const NotificationsReducer = persistReducer(
  persistConfig,
  notifications.reducer
)
